import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Button
} from '@mui/material';

import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import useVehicles from '../hooks/useVehicles';
import useDrivers from '../hooks/useDrivers';

const typeLabels = {
  plate_expiry: 'Plate expiry',
  interim_test: 'Interim test',
  booking_test: 'Booking test',
};

const typeColors = {
  plate_expiry: 'error',
  interim_test: 'info',
  booking_test: 'secondary',
};

export default function Dashboard() {
    const navigate = useNavigate()
  const { vehicles, loading: vehiclesLoading } = useVehicles();
  const { drivers, loading: driversLoading } = useDrivers();
  const now = Date.now() / 1000;

    const stats = useMemo(() => {
    const s = {
        totalVehicles: vehicles.length,
        totalDrivers: drivers.length,
        activeHires: 0,
        unassignedVehicles: 0,
        licenseExpiringSoon: 0,
        interimDueSoon: 0,
        bookingsDueSoon: 0,
        nextEvent: null,      // 👈 instead of bare timestamp
    };

    vehicles.forEach((v) => {
        if (v.driver) s.activeHires ++
    });

    s.unassignedVehicles = vehicles.length - s.activeHires;

    const upcomingEvents = [];

    vehicles.forEach((v) => {
        const phv = v.phv || {};
        const addEvent = (ts, type) => {
        if (!ts || ts <= now) return; // only future
        upcomingEvents.push({
            when: ts,          // unix seconds
            type,              // 'plate_expiry' | 'interim_test' | 'booking_test'
            vrm: v.vrm,
            vehicleId: v.id,
        });
        };

        // counters + events
        if (phv.plate_expiry_timestamp) {
        const diffDays = (phv.plate_expiry_timestamp - now) / 86400;
        if (diffDays <= 35 && diffDays >= 0) s.licenseExpiringSoon++;
        addEvent(phv.plate_expiry_timestamp, 'plate_expiry');
        }

        if (phv.interim_test_timestamp) {
        const diffDays = (phv.interim_test_timestamp - now) / 86400;
        if (diffDays <= 7 && diffDays >= 0) s.interimDueSoon++;
        addEvent(phv.interim_test_timestamp, 'interim_test');
        }

        if (phv.booking_test_timestamp) {
        const diffDays = (phv.booking_test_timestamp - now) / 86400;
        if (diffDays <= 7 && diffDays >= 0) s.bookingsDueSoon++;
        addEvent(phv.booking_test_timestamp, 'booking_test');
        }
    });

    if (upcomingEvents.length > 0) {
        upcomingEvents.sort((a, b) => a.when - b.when);
        s.nextEvent = upcomingEvents[0];   // 👈 store full event
    }

    return s;
    }, [vehicles, drivers, now]);

    const formatDateTime = (unixSeconds) =>
    new Date(unixSeconds * 1000).toLocaleString('en-GB');

    const daysUntil = (unixSeconds) =>
    Math.max(
        0,
        Math.round((unixSeconds - now) / 86400)
    );

  if (vehiclesLoading || driversLoading) {
    return (
      <Box sx={{ mt: 5, textAlign: 'center' }}>
        <Typography>Loading dashboard…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Fleet Dashboard
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Overview of your fleet, PHV health and driver activity.
        </Typography>
      </Box>

      {/* Stats Row */}
      <Grid container spacing={3}>
        <Grid item size={{xs: 12, md: 3}}>
          <StatCard
            title="Total Vehicles"
            value={stats.totalVehicles}
            icon={<DirectionsCarIcon fontSize="large" />}
            color="#1976d2"
          />
        </Grid>

        <Grid item size={{xs: 12, md: 3}}>
          <StatCard
            title="Total Drivers"
            value={stats.totalDrivers}
            icon={<PeopleIcon fontSize="large" />}
            color="#9c27b0"
          />
        </Grid>

        <Grid item size={{xs: 12, md: 3}}>
          <StatCard
            title="Active Hires"
            value={stats.activeHires}
            subtitle="Drivers assigned to vehicles"
            icon={<AssignmentTurnedInIcon fontSize="large" />}
            color="#2e7d32"
          />
        </Grid>

        <Grid item size={{xs: 12, md: 3}}>
          <StatCard
            title="Unassigned Vehicles"
            value={stats.unassignedVehicles}
            icon={<EventBusyIcon fontSize="large" />}
            color="#ed6c02"
          />
        </Grid>

        {/* Section Break */}
        <Grid item size={{xs: 12}}>
          <Section title="PHV Licensing Health" />
        </Grid>

        <Grid item size={{xs: 12, md: 4}}>
          <StatCard
            title="Licenses Expiring (≤35 days)"
            value={stats.licenseExpiringSoon}
            icon={<WarningAmberIcon fontSize="large" />}
            color="#d32f2f"
          />
        </Grid>

        <Grid item size={{xs: 12, md: 4}}>
          <StatCard
            title="Interim Tests Due (≤7 days)"
            value={stats.interimDueSoon}
            icon={<CalendarMonthIcon fontSize="large" />}
            color="#0288d1"
          />
        </Grid>

        <Grid item size={{xs: 12, md: 4}}>
          <StatCard
            title="Booking Tests Due (≤7 days)"
            value={stats.bookingsDueSoon}
            icon={<CalendarMonthIcon fontSize="large" />}
            color="#7b1fa2"
          />
        </Grid>

        <Grid size={{xs: 12}}>
          <Section title="Next Due Date" />
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, height: '100%' }} elevation={2}>
            <CardContent>
{stats.nextEvent ? (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    
    {/* Header row: type + VRM */}
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Chip
        label={typeLabels[stats.nextEvent.type]}
        color={typeColors[stats.nextEvent.type]}
        size="small"
        sx={{ fontWeight: 600 }}
      />

      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {stats.nextEvent.vrm}
      </Typography>
    </Box>

    {/* Main due date */}
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {formatDateTime(stats.nextEvent.when)}
    </Typography>

    {/* Days until */}
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      Due in <strong>{daysUntil(stats.nextEvent.when)}</strong> days
    </Typography>

    <Divider sx={{ my: 1 }} />

    {/* Actions */}
    <Box sx={{ display: "flex", gap: 1 }}>
      <Button
        size="small"
        variant="contained"
        sx={{ textTransform: "none" }}
        onClick={() => navigate(`/vehicles/${stats.nextEvent.vehicleId}`)}
      >
        View Vehicle
      </Button>

      <Button
        size="small"
        variant="outlined"
        sx={{ textTransform: "none" }}
        onClick={() =>
          navigate(
            `/calendar?date=${new Date(stats.nextEvent.when * 1000).toISOString()}`
          )
        }
      >
        View in Calendar
      </Button>
    </Box>

  </Box>
) : (
  <Typography>No upcoming deadlines.</Typography>
)}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

/* --- COMPONENTS --- */

function StatCard({ title, value, subtitle, icon, color }) {
  return (
    <Card
      elevation={3}
      sx={{
        borderRadius: 3,
        height: '100%',
        p: 1,
      }}
    >
      <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '12px',
            backgroundColor: color,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 32, fontWeight: 700 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function Section({ title }) {
  return (
    <Box sx={{ mt: 4, mb: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Divider sx={{ mt: 1, mb: 2 }} />
    </Box>
  );
}
